import { useCallback, useEffect, useState } from 'react';
import {
  BreadcrumbComponent,
  Datepicker,
  Spinner,
} from '../../components/shared';
import { TBusinessType } from '../../entities/general.entities';
import { sendSingleRequest } from '../../apis';
import { useDispatch, useSelector } from 'react-redux';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { translate } from '../../lib/lang.helper';
import {
  defaultDateFormat,
  displayValueUnit,
  pcsToDoz,
  toSecond,
} from '../../lib/common.helpers';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import { selectAccount } from '../../store/auth/auth.selector';

const formatQuantity = (type: TBusinessType, weight: number, unitType: any) => {
  if (type === 'MUSSEL') {
    return displayValueUnit(weight, unitType, 'weight_kg');
  }
  if (type === 'OYSTER') {
    return displayValueUnit(pcsToDoz(weight), unitType, 'dz');
  }
  return displayValueUnit(weight, unitType, 'weight_kg');
};

interface IFilter {
  rangeFrom: number;
  rangeTo: number;
}

interface INode {
  spat_id: number;
  spat_source: string;
  seed_type: string | null;
  crop_type: TBusinessType;
  seeded_date: number;
  stage: number;
  from_line_id: number | null;
  to_line_id: number;
  quantity: number;
  children: INode[];
}

const TreeNode: React.FC<{
  node: INode;
  level: number;
  isLast?: boolean;
}> = ({ node, level, isLast = false }) => {
  const farms = useSelector(selectFarmsData);
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const getLineInfo = (lineId: number | null) => {
    if (!lineId || !farms) return '-';

    for (const farm of farms) {
      const line = farm.lines.find(l => l.id === lineId);
      if (line) {
        return `${farm.name} - ${line.line_name}`;
      }
    }
    return `Line ID: ${lineId}`;
  };

  const indentStyle = {
    marginLeft: `${level * 40}px`,
    position: 'relative' as const,
  };

  const verticalLineStyle =
    level > 0
      ? {
          position: 'absolute' as const,
          left: `${level * 40 - 20}px`,
          top: '0',
          bottom: isLast ? '30px' : '0',
          width: '2px',
          backgroundColor: '#e8e8e8',
        }
      : {};

  const horizontalLineStyle =
    level > 0
      ? {
          position: 'absolute' as const,
          left: `${level * 40 - 20}px`,
          top: '30px',
          width: '20px',
          height: '2px',
          backgroundColor: '#e8e8e8',
        }
      : {};

  const nodeIconStyle =
    level > 0
      ? {
          position: 'absolute' as const,
          left: `${level * 40 - 26}px`,
          top: '24px',
          width: '12px',
          height: '12px',
          backgroundColor: '#4CAF50',
          borderRadius: '50%',
          border: '2px solid #fff',
          boxShadow: '0 0 0 2px #e8e8e8',
        }
      : {};

  return (
    <div className='pos-relative'>
      {level > 0 && <div style={verticalLineStyle}></div>}
      {level > 0 && <div style={horizontalLineStyle}></div>}
      {level > 0 && <div style={nodeIconStyle}></div>}
      <div style={indentStyle} className='mb-16'>
        <div className='white-card p-20'>
          <div className='d-flex justify-between align-center mb-12'>
            <div className='d-flex align-center'>
              <div className='mr-12'>
                {node.seed_type && (
                  <span
                    className='mr-8'
                    style={{
                      fontSize: '14px',
                      color: '#7f8c8d',
                      backgroundColor: '#ecf0f1',
                      padding: '2px 8px',
                      borderRadius: '12px',
                    }}
                  >
                    {node.seed_type}
                  </span>
                )}
                <strong style={{ fontSize: '16px', color: '#2c3e50' }}>
                  {node.spat_source}
                </strong>
              </div>
              <div
                style={{
                  backgroundColor:
                    node.crop_type === 'MUSSEL'
                      ? '#3498db'
                      : node.crop_type === 'OYSTER'
                        ? '#e67e22'
                        : '#27ae60',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: '600',
                }}
              >
                {node.crop_type}
              </div>
            </div>
          </div>

          <div className='d-flex flex-wrap' style={{ gap: '20px' }}>
            <div className='mb-8' style={{ minWidth: '200px' }}>
              <div
                style={{
                  fontSize: '12px',
                  color: '#7f8c8d',
                  marginBottom: '4px',
                }}
              >
                {translate(lang, 'Transfer Date')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {defaultDateFormat(node.seeded_date)}
              </div>
            </div>

            <div className='mb-8' style={{ minWidth: '200px' }}>
              <div
                style={{
                  fontSize: '12px',
                  color: '#7f8c8d',
                  marginBottom: '4px',
                }}
              >
                {translate(lang, 'Harvested Location')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {getLineInfo(node.from_line_id)}
              </div>
            </div>

            <div className='mb-8' style={{ minWidth: '200px' }}>
              <div
                style={{
                  fontSize: '12px',
                  color: '#7f8c8d',
                  marginBottom: '4px',
                }}
              >
                {translate(lang, 'Transfer Destination')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {getLineInfo(node.to_line_id)}
              </div>
            </div>

            <div className='mb-8' style={{ minWidth: '150px' }}>
              <div
                style={{
                  fontSize: '12px',
                  color: '#7f8c8d',
                  marginBottom: '4px',
                }}
              >
                {translate(lang, 'Quantity')}
              </div>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#27ae60',
                }}
              >
                {formatQuantity(node.crop_type, node.quantity, unitType)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {node.children && node.children.length > 0 && (
        <div>
          {node.children.map((child, index) => (
            <TreeNode
              key={`${child.spat_id}-${index}`}
              node={child}
              level={level + 1}
              isLast={index === node.children.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BatchTransfersPage = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);

  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState<INode[]>([]);
  const [filter, setFilter] = useState<IFilter>({
    rangeFrom: new Date(new Date().getFullYear(), 0, 1).getTime(),
    rangeTo: Date.now(),
  });

  const breadcrumbItems = getBreadcrumbMenu('SPAT_TRANSFERS', lang);

  const changeFilter = (key: keyof IFilter, value: number | null) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const response = await sendSingleRequest(
      {},
      'GET',
      'api/farm/spats-transfers',
      true,
    );
    if (response.status) {
      setTreeData(response.data);
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          message: response.data?.message || 'Unknown error',
          type: 'error',
          duration: -1,
        }),
      );
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isOK = (node: INode) => {
    if (
      toSecond(filter.rangeFrom) <= node.seeded_date &&
      node.seeded_date <= toSecond(filter.rangeTo)
    ) {
      return true;
    }
    if (node.children && node.children.some(x => isOK(x))) {
      return true;
    }
    return false;
  };

  const filtered = treeData.filter(node => isOK(node));

  return (
    <div className='h-calc-80 bg-secondary spat-storages-page'>
      {loading && (
        <div className='loader'>
          <Spinner />
        </div>
      )}
      <div className='container pos-relative'>
        <div className='d-flex justify-between align-center px-20 pt-24 pb-12'>
          <BreadcrumbComponent items={breadcrumbItems} />
        </div>
        <div className='p-20 pt-0'>
          <div className='d-flex justify-end'>
            <div className='d-flex'>
              <Datepicker
                label={translate(lang, 'Date from')}
                defaultValue={filter.rangeFrom}
                onChange={e =>
                  changeFilter('rangeFrom', e?.toDate().getTime() ?? Date.now())
                }
                className='mr-7'
              />
              <Datepicker
                label={translate(lang, 'Date to')}
                defaultValue={filter.rangeTo}
                onChange={e =>
                  changeFilter('rangeTo', e?.toDate().getTime() ?? Date.now())
                }
                className='ml-7'
              />
            </div>
          </div>
          {!loading && filtered.length === 0 && (
            <div className='white-card p-40' style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', color: '#7f8c8d' }}>
                {translate(lang, 'No data available')}
              </div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div>
              {filtered.map((rootNode, index) => (
                <TreeNode
                  key={`${rootNode.spat_id}-${index}`}
                  node={rootNode}
                  level={0}
                  isLast={index === filtered.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchTransfersPage;
